import { Injectable } from '@angular/core';
import moment from 'moment';
import { ApiService } from '@app/services';
import { WorkingHoursData, StorePlanning, EventPlanning, EventPlanningExtendedProp, EventPlanningObjectives, PearlsPlanningDeleteAgent } from '@app/models';

@Injectable({
  providedIn: 'root'
})
export class PermanentPlanningService {

    constructor(
        private apiService: ApiService
    ) {}
    
    async loadWorkingHours(operationId: number, agentId: number, week: Date): Promise<WorkingHoursData|null>{
        let workingHoursData: WorkingHoursData|null = null;
        let params = {
            ctxId: operationId,
            salesRepId: agentId,
            week: moment(week).format('WW-GGGG')
        };
        let response = await this.apiService.get('sales-rep-planning/salesrep-workhours', params);
        if(response?.data){
            workingHoursData = WorkingHoursData.FromJson(response.data);
        }
        return workingHoursData;
    }
    
    async loadStores(operationId: number, agentId: number, displayAllAreaSectors: boolean): Promise<StorePlanning[]> {
        let stores: Array<StorePlanning> = [];
        let params = {
            ctxId: operationId,
            salesRepId: agentId,
            showAllArea: displayAllAreaSectors
        };
        let response = await this.apiService.get('sales-rep-planning/draggable-stores', params);
        if(response?.data){
            for(let d of response.data){
                stores.push(StorePlanning.FromJson(d));
            }
        }
        return stores;
    }

    async loadEvents(operationId: number, agentId: number, startDate: Date, endDate: Date, sectorsIds: Array<number> = [], retailersIds: Array<number> = [], rankingsIds: Array<number> = [], profilsIds: Array<number> = [], displayAllAreaSectors: boolean = false): Promise <EventPlanning[]>{
        let events: Array<EventPlanning> = [];
        let params = {
            ctxId: operationId,
            salesRep: agentId,
            start: moment(startDate).format(),
            end: moment(endDate).format(),
            showAllArea: displayAllAreaSectors
        };
       
        let body = {
            sectors: sectorsIds,
            retailers: retailersIds,
            rankings: rankingsIds,
            profiles: profilsIds
        };
        
        let response = await this.apiService.post('sales-rep-planning/events', body, params);
        if(response?.data){
            for(let d of response.data){
                events.push(EventPlanning.FromJson(d));
            }
        }
        
        let p = new EventPlanningExtendedProp();
                    
        events.sort((a, b) => {
            return new Date(a.start).getTime() - new Date(b.start).getTime();
        });
        
        return events;
    }
    
    async createVisit(operationId: number, agentId: number, storeId: number, start: Date, end: Date, refDate: number): Promise<number|null> {
        let storeCheckId: number|null = null;        
        let params = {
            ctxId: operationId,
            salesRepId: agentId,
            storeId: storeId,
            start: start.getTime(),
            end: end.getTime(),
            refDate: refDate
        };        
        let body = {};        
        let response = await this.apiService.post('storecheck', body, params);  
        if(response?.data){
            storeCheckId = response.data;
        }
        return storeCheckId;      
    }
    
    async updateStoreCheck(operationId: number, storeCheckId: number, start: Date, end: Date, refDate: number){
        let params = {
            ctxId: operationId,
            start: start.getTime(),
            end: end.getTime(),
            refDate: refDate
        };        
        let body = {};        
        return await this.apiService.put('storecheck/' + storeCheckId + '/period', body, params);
    }
    
    async updateSalesRepEvent(operationId: number, eventId: number, start: Date, end: Date, refDate: number){
        let params = {
            ctxId: operationId,
            start: start.getTime(),
            end: end.getTime(),
            refDate: refDate
        };        
        let body = {};        
        return await this.apiService.put('event/' + eventId + '/period', body, params);
    }
    
    async deleteStoreCheck(operationId: number, storeCheckId: number): Promise<boolean>{
        let params = {
            ctxId: operationId
        };
        let response = await this.apiService.delete('storecheck/' + storeCheckId, params);        
        return (response.error === null);
    }
    
    async deleteSalesRepEvent(operationId: number, eventId: number): Promise<boolean>{
        let params = {
            ctxId: operationId
        };
        let response = await this.apiService.delete('event/' + eventId, params);  
        return (response.error === null);      
    }
    
    async duplicatePlanning(operationId: number, agentsId: Array<number>, days: Array<number>, destWeeks: Array<string>, copyEvents: boolean): Promise<boolean>{
        let statusDuplicate = false;
        let params = {
            ctxId: operationId
        };
        let body: any = {
            referenceDateToCopy: days,
            destinationWeekNumber: destWeeks,
            salesReps: agentsId,
            copyEvents: copyEvents
        }; 
        let response = await this.apiService.post('sales-rep-planning/duplicate', body, params);   
        if(response?.data){
            statusDuplicate = response.data;
        }   
        return statusDuplicate;
    }
    
    async loadPlanningItemsToDelete(operationId: number, startDate: Date, endDate: Date, agentsId: Array<number>, includeEvents: boolean): Promise<PearlsPlanningDeleteAgent[]> {
        let pearlsPlanningDeleteAgents: Array<PearlsPlanningDeleteAgent> = [];
        let params = {
            ctxId: operationId,
            startPeriod: moment(startDate).format('YYYY-MM-DD'),
            endPeriod: moment(endDate).format('YYYY-MM-DD'),
            salesReps: agentsId
        };
        let response = await this.apiService.get('sales-rep-planning/deletable', params);
        if(response?.data){
            pearlsPlanningDeleteAgents = PearlsPlanningDeleteAgent.FromJson(response.data, includeEvents);
        }
        return pearlsPlanningDeleteAgents;
    }
    
    async deletePlanning(operationId: number, storeCheckIds: Array<number>, eventIds: Array<number>) : Promise<boolean>{
        let statusDelete = false;
        let params = {
            ctxId: operationId,
            storeCheckIds: storeCheckIds,
            eventIds: eventIds
        };
        let response = await this.apiService.delete('sales-rep-planning', params);
        if(!response.error){
            statusDelete = true;
        }
        return statusDelete;
    }
}
